/**
 * MenuBarLogo — RunAI Apple-style menü (sol üst)
 * Her şey burada: user info + profile + classic dashboard + settings + admin + theme + power
 * Sağdaki user dropdown kaldırıldı (chat sağa alındığında taşıyordu).
 */
import { AlertCircle, Bot, Info, LayoutDashboard, Lock, LogOut, Moon, Power, RotateCcw, Settings, Shield, Sun, User } from 'lucide-react'
import { Suspense, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { useOSTheme } from '../OSThemeContext'
import { useWindowManager } from '../WindowManager/WindowManagerContext'
import { WINDOW_REGISTRY } from '../WindowManager/windowRegistry'

export default function MenuBarLogo() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { theme, toggle } = useOSTheme()
  const { openWindow } = useWindowManager()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const openUserHub = () => {
    const Comp = WINDOW_REGISTRY['user-hub']
    if (!Comp) return
    const vw = window.innerWidth, vh = window.innerHeight
    const W = Math.min(960, Math.floor(vw * 0.85))
    const H = Math.min(640, Math.floor(vh * 0.82))
    const Wrapped = () => (
      <Suspense fallback={<div style={{ padding: 40, color: 'var(--text-tertiary)', fontSize: 12 }}>Loading...</div>}>
        <Comp />
      </Suspense>
    )
    openWindow({
      id: 'user-hub', title: 'User Hub', component: Wrapped,
      position: { x: Math.floor((vw - W) / 2), y: Math.floor((vh - H) / 2) },
      size: { width: W, height: H }, noPadding: true,
    })
  }

  const item = (icon: React.ReactNode, label: string, onClick: () => void, color?: string) => (
    <button key={label} onClick={() => { onClick(); setOpen(false) }} style={{
      display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 12px',
      background: 'transparent', border: 'none', color: color ?? 'var(--os-text)',
      fontSize: 12, cursor: 'pointer', textAlign: 'left', borderRadius: 6, fontFamily: 'inherit',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--os-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >{icon}{label}</button>
  )

  const divider = <div style={{ borderTop: '1px solid var(--os-border)', margin: '4px 0' }} />

  return (
    <div ref={ref} style={{ position: 'relative', marginRight: 12 }}>
      <button onClick={() => setOpen(p => !p)} style={{
        display: 'flex', alignItems: 'center', gap: 5, padding: '2px 8px',
        background: open ? 'var(--os-hover)' : 'transparent', border: 'none',
        cursor: 'pointer', borderRadius: 4, color: 'var(--os-text)',
      }}>
        <Bot size={15} color="#6366f1" />
        <span style={{
          fontWeight: 800, fontSize: 13,
          background: 'linear-gradient(135deg,#6366f1,#ec4899)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>RunAI</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 28, left: 0, minWidth: 230, padding: 5,
          background: 'var(--os-win-bg)', backdropFilter: 'blur(20px)',
          border: '1px solid var(--os-border)', borderRadius: 10,
          boxShadow: 'var(--os-win-shadow)', zIndex: 9999,
        }}>
          {/* User info header */}
          {user && (
            <>
              <div style={{ padding: '8px 12px 6px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'linear-gradient(135deg,#818cf8,#c084fc)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 800, color: '#fff', flexShrink: 0,
                }}>
                  {(user.display_name ?? user.username ?? 'U').charAt(0).toUpperCase()}
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--os-text)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{String(user.display_name ?? user.email)}</div>
                  <div style={{ fontSize: 10, color: 'var(--os-muted)' }}>@{user.username ?? user.email?.split('@')[0] ?? 'user'}</div>
                </div>
              </div>
              {divider}
            </>
          )}

          {item(<Info size={13} />, 'About RunAI WebOS', () => navigate('/'))}
          {item(<Bot size={13} />, 'RunAI Website', () => window.open('https://runai.live', '_blank'), '#6366f1')}
          {divider}

          {user && item(<User size={13} />, 'User Hub', openUserHub, '#818cf8')}
          {user && item(<User size={13} />, 'Public Profile', () => navigate(`/profile/${user.username ?? user.id}`))}
          {item(<LayoutDashboard size={13} />, 'Dashboard', () => navigate('/dashboard'))}
          {item(<Settings size={13} />, 'Settings', () => {
            const Comp = WINDOW_REGISTRY['os-settings']
            if (!Comp) return
            const vw = window.innerWidth, vh = window.innerHeight
            openWindow({
              id: 'os-settings', title: 'Settings',
              component: () => (<Suspense fallback={<div style={{ padding: 40, fontSize: 12 }}>Loading...</div>}><Comp /></Suspense>),
              position: { x: Math.floor((vw - 640) / 2), y: Math.floor((vh - 480) / 2) },
              size: { width: 640, height: 480 },
            })
          })}
          {user && user.role === 'admin' && item(<Shield size={13} />, 'Admin Panel', () => {
            const Comp = WINDOW_REGISTRY['admin-panel']
            if (!Comp) { navigate('/dashboard'); return }
            const vw = window.innerWidth, vh = window.innerHeight
            const W = Math.min(1100, Math.floor(vw * 0.9))
            const H = Math.min(700, Math.floor(vh * 0.85))
            openWindow({
              id: 'admin-panel', title: 'Admin Panel',
              component: () => (<Suspense fallback={<div style={{ padding: 40, fontSize: 12 }}>Loading...</div>}><Comp /></Suspense>),
              position: { x: Math.floor((vw - W) / 2), y: Math.floor((vh - H) / 2) },
              size: { width: W, height: H }, noPadding: true,
            })
          }, '#f59e0b')}
          {item(<AlertCircle size={13} />, 'Help & Support', () => navigate('/support'))}

          {divider}
          {item(theme === 'dark' ? <Sun size={13} /> : theme === 'light' ? <Moon size={13} /> : <RotateCcw size={13} />, 
            theme === 'dark' ? 'Light Mode' : theme === 'light' ? 'System' : 'Dark Mode', 
            toggle)}

          {divider}
          {item(<Lock size={13} />, 'Lock Screen', () => navigate('/login'))}
          {item(<RotateCcw size={13} />, 'Restart', () => window.location.reload())}
          {item(<Power size={13} />, 'Shut Down', () => { if (confirm('Sign out?')) { logout(); navigate('/login') } }, '#ef4444')}
          {user && item(<LogOut size={13} />, 'Sign Out', () => { logout(); navigate('/login') }, '#ef4444')}
        </div>
      )}
    </div>
  )
}
